"use client";

import Link from "next/link";
import { MessageCircleMore } from "lucide-react";

import {
  useLocale,
  useLocalizedText,
} from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { WhatsAppCta } from "@/components/shared/WhatsAppCta";

interface ConsultationCTAProps {
  title: string;
  description: string;
  buttonText?: string;
  primaryLabel?: string;
  primaryHref?: string;
  className?: string;
  /**
   * Optional WhatsApp button alongside the primary CTA.
   * Omit on /singapore/ pages (the SG number is not final).
   */
  whatsapp?: { message: string; location: string; label?: string };
  /** GA4 location for the primary button. GA4 adds the page URL, so the default is enough. */
  trackLocation?: string;
}

export default function ConsultationCTA({
  title,
  description,
  buttonText = "Schedule a consultation",
  primaryLabel,
  primaryHref,
  className = "",
  whatsapp,
  trackLocation = "consultation_cta",
}: ConsultationCTAProps) {
  const locale = useLocale();
  const ctaLabel = primaryLabel ?? buttonText;
  const href = primaryHref;
  const isExternalLink = href ? /^https?:\/\//.test(href) : false;
  const resolvedHref = href
    ? isExternalLink
      ? href
      : getLocalizedPathname(href, locale)
    : getLocalizedPathname("/contact/", locale);
  const translatedTitle = useLocalizedText(title);
  const translatedDescription = useLocalizedText(description);
  const translatedCtaLabel = useLocalizedText(ctaLabel);

  return (
    /**
     * Self-contained: the section, the max-width container and the vertical
     * rhythm all live here, so every page renders this band identically and a
     * caller only supplies content.
     *
     * It used to be a bare card and relied on each caller to wrap it. Most did
     * not: measured at 1440px viewport, the guides rendered it 1440px wide with
     * 0 margin and 0 gap above, while blog/product/service pages rendered it
     * 1216px with 112px either side. Same component, two different designs.
     */
    <section className={`bg-background py-16 lg:py-20 ${className}`}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl bg-hero-background px-6 py-12 text-center md:px-12 md:py-16">
      <p className="text-sm font-semibold uppercase tracking-[0.35em] text-primary-contrast/60">
        {t(locale, "Free consultation")}
      </p>
      <h3 className="mt-4 font-display text-3xl font-bold tracking-tight text-primary-contrast md:text-4xl">
        {translatedTitle}
      </h3>
      <p className="mx-auto mt-5 max-w-2xl text-primary-contrast/70">
        {translatedDescription}
      </p>

      <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
        {/* Primary CTA - solid pill */}
        <Link
          href={resolvedHref}
          data-track-event="cta_click"
          data-track-location={trackLocation}
          data-track-destination={href ?? "/contact/"}
          target={isExternalLink ? "_blank" : undefined}
          rel={isExternalLink ? "noopener noreferrer" : undefined}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 font-semibold text-primary-foreground transition hover:bg-primary-emphasis sm:min-w-[220px]"
        >
          <MessageCircleMore className="h-5 w-5" aria-hidden />
          {translatedCtaLabel}
        </Link>

        {whatsapp ? (
          <WhatsAppCta
            message={whatsapp.message}
            location={whatsapp.location}
            label={t(locale, whatsapp.label ?? "WhatsApp us")}
            className="px-8 py-3.5 text-base sm:min-w-[220px]"
          />
        ) : null}

        </div>
        </div>
      </div>
    </section>
  );
}
